import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRightIcon, PencilIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { WizardChrome, WizardFooter } from '../../components/wizard/WizardChrome';
import { Button } from '../../components/ui/Button';
import { SearchInput } from '../../components/ui/SearchInput';
import { Checkbox, Pagination, Table, TD, TH, THead, TR } from '../../components/ui/Table';
import { AccordionCard } from '../../components/ui/AccordionCard';
import { Field, TextInput } from '../../components/ui/Field';
import { Select } from '../../components/ui/Select';
import { NestedSelect } from '../../components/ui/NestedSelect';
import { DatePicker, toReviewDate } from '../../components/ui/DatePicker';
import { devices } from '../../data/devices';
import { studentById } from '../../data/students';
import { issueTypeGroups, operabilityOptions } from '../../data/issueTypes';
import { usePagination } from '../../hooks/usePagination';
const steps = ['Select Device', 'Add Device Details', 'Review Claim'];
interface Draft {
  typeOfIssue: string;
  dateOfIssue: string;
  description: string;
  itemOperability: string;
}
const emptyDraft: Draft = {
  typeOfIssue: '',
  dateOfIssue: '',
  description: '',
  itemOperability: ''
};
const claimableDevices = devices;
function isComplete(draft: Draft): boolean {
  return Boolean(draft.typeOfIssue && draft.dateOfIssue && draft.description.trim() && draft.itemOperability);
}
export function FileClaim() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [openCard, setOpenCard] = useState<string | null>(null);
  const [validated, setValidated] = useState(false);
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return claimableDevices;
    return claimableDevices.filter(device => {
      const student = studentById(device.studentId);
      return `${device.make} ${device.model} ${device.serialNumber} ${device.assetTagId} ${student?.name ?? ''}`.toLowerCase().includes(q);
    });
  }, [query]);
  const {
    page,
    setPage,
    totalPages,
    pageItems
  } = usePagination(rows, 20);
  const enabledOnPage = pageItems.filter(device => !device.activeClaim);
  const allEnabledSelected = enabledOnPage.length > 0 && enabledOnPage.every(device => selected.includes(device.id));
  const selectedDevices = claimableDevices.filter(device => selected.includes(device.id));
  function toggle(id: string) {
    setSelected(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  }
  function toggleAll() {
    if (allEnabledSelected) {
      setSelected(prev => prev.filter(id => !enabledOnPage.some(device => device.id === id)));
    } else {
      setSelected(prev => [...prev, ...enabledOnPage.filter(device => !prev.includes(device.id)).map(device => device.id)]);
    }
  }
  function draftFor(id: string): Draft {
    return drafts[id] ?? emptyDraft;
  }
  function updateDraft(id: string, patch: Partial<Draft>) {
    setDrafts(prev => ({
      ...prev,
      [id]: {
        ...emptyDraft,
        ...prev[id],
        ...patch
      }
    }));
  }
  function goToStep2() {
    setStep(2);
    setValidated(false);
    setOpenCard(selected[0] ?? null);
  }
  function proceedToReview() {
    const allValid = selectedDevices.every(device => isComplete(draftFor(device.id)));
    if (!allValid) {
      setValidated(true);
      return;
    }
    setValidated(false);
    setStep(3);
  }
  function removeRow(id: string) {
    const next = selected.filter(s => s !== id);
    setSelected(next);
    if (next.length === 0) setStep(1);
  }
  return <>
      <WizardChrome title="File New Claim" steps={steps} currentStep={step} onClose={() => navigate('/claims')} data-id="element-756" data-mp="sj4n5dnzdwgv" />

      <div className="flex flex-1 flex-col bg-white px-6 py-5" data-id="element-757" data-mp="3gi63c0xbt1my">
        {step === 1 && <>
            <h2 className="text-[15px] font-bold text-ink-900" data-id="element-758" data-mp="3lffo339rsp4g">Select Devices</h2>
            <p className="mt-1 text-[13px] text-ink-500" data-id="element-759" data-mp="37f4y02gabekc">
              Please select the devices for which you want to file claim
            </p>

            <div className="mt-4" data-id="element-760" data-mp="3ch40zb8449gr">
              <SearchInput value={query} onChange={setQuery} placeholder="Search devices by IMEI/SN or asset tag ID" className="w-[260px]" data-id="element-761" data-mp="2xq4vwa6hk31x" />
            </div>

            <div className="mt-4 flex flex-1 flex-col" data-id="element-762" data-mp="2or9mkp85acue">
              <Table minWidth={1000} data-id="element-763" data-mp="2sqsazuz6d61z">
                <THead data-id="element-764" data-mp="2srtjpsspxggj">
                  <tr data-id="element-765" data-mp="2soxmy7slwxto">
                    <TH width={44} data-id="element-766" data-mp="2ersniqneex7e">
                      <Checkbox checked={allEnabledSelected} onChange={toggleAll} label="Select all available devices" data-id="element-767" data-mp="2estvgm94b3kw" />
                    </TH>
                    <TH data-id="element-768" data-mp="206n3weaw4t29">Device Make &amp; Model</TH>
                    <TH data-id="element-769" data-mp="1v6sb257k1tek">Device Type</TH>
                    <TH data-id="element-770" data-mp="1v7tnad0b4lxv">Serial Number</TH>
                    <TH data-id="element-771" data-mp="2a39nz0ve7g38">Asset Tag ID</TH>
                    <TH data-id="element-772" data-mp="2a4b25e7pgip6">Student</TH>
                  </tr>
                </THead>
                <tbody data-id="element-773" data-mp="2533urg9h5lj1">
                  {pageItems.map(device => {
                const student = studentById(device.studentId);
                const disabled = device.activeClaim;
                const checked = selected.includes(device.id);
                return <TR key={device.id} disabled={disabled} selected={checked} onClick={disabled ? undefined : () => toggle(device.id)} data-id="element-774" data-mp="2ss684bx2ss7q">
                        <TD data-id="element-775" data-mp="2xsu83n3a8je3">
                          <span className="group relative inline-flex" data-id="element-776" data-mp="2xtluc7azp4rp">
                            <Checkbox checked={checked} disabled={disabled} onChange={() => toggle(device.id)} label={`Select ${device.serialNumber}`} data-id="element-777" data-mp="34w0s2ga3b7ok" />
                            {disabled && <span className="pointer-events-none absolute left-6 top-1/2 z-20 hidden w-56 -translate-y-1/2 rounded bg-ink-900 px-2.5 py-1.5 text-[11px] leading-snug text-white group-hover:block" data-id="element-778" data-mp="3jm7ytyqqj29f">
                                You can&apos;t file a claim since this device is part of an active claim
                              </span>}
                          </span>
                        </TD>
                        <TD className={disabled ? 'text-ink-400' : ''} data-id="element-779" data-mp="236om575fzjp5">
                          {device.make} {device.model}
                        </TD>
                        <TD className={disabled ? 'text-ink-400' : ''} data-id="element-780" data-mp="2861hkgadpz3c">{device.deviceType}</TD>
                        <TD className={disabled ? 'text-ink-400' : ''} data-id="element-781" data-mp="pbye0zrxl3xe">{device.serialNumber}</TD>
                        <TD className={disabled ? 'text-ink-400' : ''} data-id="element-782" data-mp="ki3xg57mh52j">{device.assetTagId}</TD>
                        <TD className={disabled ? 'text-ink-400' : ''} data-id="element-783" data-mp="giv594s8df93">
                          {student?.name ?? 'No Student Assigned'}
                        </TD>
                      </TR>;
              })}
                </tbody>
              </Table>
              <Pagination page={page} totalPages={totalPages} onChange={setPage} data-id="element-784" data-mp="147ecri3gho04" />
            </div>
          </>}

        {step === 2 && <>
            <h2 className="text-[15px] font-bold text-ink-900" data-id="element-785" data-mp="u9gq2z18vz9m">Add Device Details</h2>
            <p className="mt-1 text-[13px] text-ink-500" data-id="element-786" data-mp="ubkudi3230af">
              Provide the necessary device information to support your claim
            </p>

            <div className="mt-4 space-y-3 pb-8" data-id="element-787" data-mp="3eynwzi5xbiek">
              {selectedDevices.map(device => {
            const draft = draftFor(device.id);
            const student = studentById(device.studentId);
            const invalid = validated && !isComplete(draft);
            const showError = (value: string) => validated && !value.trim();
            return <AccordionCard key={device.id} title={`${device.make} ${device.model}`} subtitle={`Student: ${student?.name ?? 'No Student Assigned'} • Serial Number: ${device.serialNumber}`} open={openCard === device.id} onToggle={() => setOpenCard(openCard === device.id ? null : device.id)} error={invalid ? 'Missing Device Details' : undefined} sectionLabel="Claim Details" data-id="element-788" data-mp="1dwofcfiiobae">
                    <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2" data-id="element-789" data-mp="2pcd51en5noew">
                      <Field label="Type of Issue" required error={showError(draft.typeOfIssue) ? 'Type of Issue is required' : undefined} data-id="element-790" data-mp="dxnye3jchutt">
                        <NestedSelect groups={issueTypeGroups} value={draft.typeOfIssue} placeholder="Select Type of Issue" onChange={value => updateDraft(device.id, {
                    typeOfIssue: value
                  })} error={showError(draft.typeOfIssue)} data-id="element-791" data-mp="nmfkvw5mzmxc" />
                      </Field>

                      {draft.typeOfIssue && <Field label="Item Operability" required htmlFor={`oper-${device.id}`} error={showError(draft.itemOperability) ? 'Item Operability is required' : undefined} data-id="element-792" data-mp="3w3ahwuzr6d3i">
                          <Select id={`oper-${device.id}`} fullWidth value={draft.itemOperability} onChange={value => updateDraft(device.id, {
                    itemOperability: value
                  })} options={operabilityOptions} placeholder="Select Item Operability" error={showError(draft.itemOperability)} data-id="element-793" data-mp="4gkvf9hdrylf" />
                        </Field>}

                      <Field label="Date of Issue" required error={showError(draft.dateOfIssue) ? 'Date of Issue is required' : undefined} data-id="element-794" data-mp="38uswbv7smhol">
                        <DatePicker value={draft.dateOfIssue} onChange={value => updateDraft(device.id, {
                    dateOfIssue: value
                  })} error={showError(draft.dateOfIssue)} data-id="element-795" data-mp="173g15xvmno5q" />
                      </Field>

                      <Field label="Description of Incident" required htmlFor={`desc-${device.id}`} error={showError(draft.description) ? 'Description of Incident is required' : undefined} data-id="element-796" data-mp="1elw4ksd011dp">
                        <TextInput id={`desc-${device.id}`} value={draft.description} onChange={value => updateDraft(device.id, {
                    description: value
                  })} placeholder="Eg. dropped on the floor, water spilled on keyboard, back hinge broken etc." error={showError(draft.description)} data-id="element-797" data-mp="vpxkmyu369hr" />
                      </Field>
                    </div>
                  </AccordionCard>;
          })}
            </div>
          </>}

        {step === 3 && <>
            <h2 className="text-[15px] font-bold text-ink-900" data-id="element-798" data-mp="c2zagawbmk7w">Review Claim</h2>
            <p className="mt-1 text-[13px] text-ink-500" data-id="element-799" data-mp="q04c7yp04wxc">
              Review and confirm the details before submitting the claim
            </p>

            <div className="mt-4" data-id="element-800" data-mp="3th3d1kgnwzyi">
              <Table minWidth={1360} data-id="element-801" data-mp="3tief9fuglzni">
                <THead data-id="element-802" data-mp="3tfa6e561ufqi">
                  <tr data-id="element-803" data-mp="3pg1aatnje5rs">
                    <TH data-id="element-804" data-mp="3pgkl7qerrtwb">Device Make &amp; Model</TH>
                    <TH data-id="element-805" data-mp="3pdyaxj0hv0ae">Student</TH>
                    <TH data-id="element-806" data-mp="72bi9lwcvxc3">Serial Number</TH>
                    <TH data-id="element-807" data-mp="73362ajpoqrt">Asset Tag ID</TH>
                    <TH data-id="element-808" data-mp="2bbpmsu9s1fe">Type of Issue</TH>
                    <TH data-id="element-809" data-mp="2aa39s7szsid">Date of Issue</TH>
                    <TH data-id="element-810" data-mp="298rtmmvl404">Description of Issue</TH>
                    <TH data-id="element-811" data-mp="3aptt0h99msso">Item Operability</TH>
                    <TH width={90} data-id="element-812" data-mp="3aoiougbwrj11">Actions</TH>
                  </tr>
                </THead>
                <tbody data-id="element-813" data-mp="35pph74jaym8s">
                  {selectedDevices.map(device => {
                const draft = draftFor(device.id);
                const student = studentById(device.studentId);
                return <TR key={device.id} data-id="element-814" data-mp="1c9l6sfewgsqs">
                        <TD data-id="element-815" data-mp="1camkkrnavtc7">
                          {device.make} {device.model}
                        </TD>
                        <TD data-id="element-816" data-mp="1h61uj8i6t53c">{student?.name ?? 'No Student Assigned'}</TD>
                        <TD data-id="element-817" data-mp="24z1uizrkrlgo">{device.serialNumber}</TD>
                        <TD data-id="element-818" data-mp="28z29qmmi00os">{device.assetTagId}</TD>
                        <TD data-id="element-819" data-mp="28ysikvppvnhi">{draft.typeOfIssue}</TD>
                        <TD data-id="element-820" data-mp="1uzt3ofs0amyj">{toReviewDate(draft.dateOfIssue)}</TD>
                        <TD data-id="element-821" data-mp="1v1nh790y7csl">{draft.description}</TD>
                        <TD data-id="element-822" data-mp="1zxunj2x9aqb2">{draft.itemOperability}</TD>
                        <TD data-id="element-823" data-mp="200gup1g8j3sr">
                          <span className="flex items-center gap-1" data-id="element-824" data-mp="jjupjxhz1aqg">
                            <button type="button" aria-label={`Edit claim details for ${device.serialNumber}`} onClick={() => {
                        setStep(2);
                        setOpenCard(device.id);
                      }} className="flex h-7 w-7 items-center justify-center rounded text-ink-500 hover:bg-ink-100 hover:text-ink-700" data-id="element-825" data-mp="jmqnhlttjzew">
                              <PencilIcon className="h-3.5 w-3.5" data-id="element-826" data-mp="1mrq5lo245swk" />
                            </button>
                            <button type="button" aria-label={`Remove ${device.serialNumber} from claim`} onClick={() => removeRow(device.id)} className="flex h-7 w-7 items-center justify-center rounded text-ink-500 hover:bg-red-50 hover:text-red-600" data-id="element-827" data-mp="1ipv3wig9tdi9">
                              <Trash2Icon className="h-3.5 w-3.5" data-id="element-828" data-mp="3thwkhvk5t9o1" />
                            </button>
                          </span>
                        </TD>
                      </TR>;
              })}
                </tbody>
              </Table>
            </div>

            <Button variant="secondary" className="mt-4" onClick={() => setStep(1)} data-id="element-829" data-mp="3tc5dy6kig4ov">
              <PlusIcon className="h-4 w-4" data-id="element-830" data-mp="1y13c77r2v9d" />
              Add Another Device
            </Button>
          </>}
      </div>

      <WizardFooter counter={step === 1 && selected.length > 0 ? <span className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-3 py-1.5 text-[12.5px] font-medium text-brand-700" data-id="element-832" data-mp="axwi0m0il8e5">
              {selected.length} {selected.length === 1 ? 'Device' : 'Devices'} Selected
              <ChevronRightIcon className="h-3.5 w-3.5" data-id="element-833" data-mp="kwdaxoj0jx2u" />
            </span> : undefined} data-id="element-831" data-mp="5ykwz0t47gqr">
        {step > 1 && <Button variant="secondary" onClick={() => setStep(step - 1)} data-id="element-834" data-mp="2mo8jhv9z3lgu">
            Back
          </Button>}
        {step === 1 && <Button disabled={selected.length === 0} onClick={goToStep2} data-id="element-835" data-mp="2eolsy1ed3a4k">
            Proceed to Add Details
          </Button>}
        {step === 2 && <Button onClick={proceedToReview} data-id="element-836" data-mp="2olqf4187j5ld">Proceed to Review Claim</Button>}
        {step === 3 && <Button onClick={() => navigate('/claims/success')} data-id="element-837" data-mp="2ojdnsxgk7ivn">File Claim</Button>}
      </WizardFooter>
    </>;
}